import { type createContactFormSchema } from "@/components/features/contact/AddOrEditContactModal/AddContactSlideover";
import {
  Button,
  Form,
  FormSubmitButton,
  PhoneNumberFormField,
  SheetFooter,
  SimpleSelectFormField,
  Slideover,
  TextFormField,
  Typography,
} from "@/components/ui";
import { FormLoadingOverlay } from "@/components/ui/Form/FormLoadingOverlay";
import { CheckboxFormField } from "@/components/ui/FormFields/CheckboxFormField/CheckboxFormField";
import { FormCancelButton } from "@/components/ui/FormFields/FormCancelButton/FormCancelButton";
import {
  useCreateContactFax,
  useUpdateContactFax,
} from "@/lib/data/react-query";
import { ContactFaxSchema } from "@/lib/data/schemas";
import { CONTACT_FAX_LABELS } from "@/lib/data/static/contactStatics";
import { type ContactFax } from "@/lib/types";
import { ContactFaxIcon } from "@/lib/utils/iconUtils";
import { cn } from "@/lib/utils/cn";
import { slideoverFieldsetClassName } from "@/lib/utils/slideover-classes";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import { useForm, type UseFormReturn } from "react-hook-form";
import type { z } from "zod";

interface AddOrEditContactFaxSlideoverProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  editing: boolean;
  data?: ContactFax;
  contactId: string;
  onDelete?: () => void;
  hasEditPermission?: boolean;
  hasDeletePermission?: boolean;
  parentForm?: UseFormReturn<z.infer<typeof createContactFormSchema>>;
  variant?: "slideover" | "inline";
}

type ConditionalProps =
  | {
      editing: true;
      activeContactFax: Pick<ContactFax, "id" | "dateModified">;
    }
  | {
      editing: false;
      activeContactFax?: never;
    };

const formSchema = ContactFaxSchema.omit({
  firmId: true,
  dateCreated: true,
  contactId: true,
  id: true,
});

const defaultObject: z.input<typeof formSchema> = {
  number: "",
  description: "",
  label: "",
  extension: "",
  isPrimary: false,
  dateModified: "",
};

export const AddOrEditContactFaxSlideover = ({
  activeContactFax,
  contactId,
  editing,
  hasDeletePermission,
  hasEditPermission,
  onDelete,
  open,
  parentForm,
  setOpen,
  variant = "slideover",
}: AddOrEditContactFaxSlideoverProps & ConditionalProps) => {
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    values: editing ? { ...defaultObject, ...activeContactFax } : defaultObject,
    mode: "onSubmit",
  });
  const createContactFax = useCreateContactFax();
  const updateContactFax = useUpdateContactFax();
  const isFormLoading =
    createContactFax.isPending || updateContactFax.isPending;

  const onSubmitParentForm = (data: z.infer<typeof formSchema>) => {
    if (!parentForm) return;
    if (editing) {
      const existingFaxes = parentForm.getValues("faxes") ?? [];
      parentForm.setValue(
        "faxes",
        existingFaxes.map((fax) =>
          fax.id === activeContactFax.id
            ? {
                ...data,
                id: activeContactFax.id,
              }
            : fax,
        ),
        {
          shouldDirty: true,
        },
      );
      return data;
    } else {
      const existingFaxes = parentForm.getValues("faxes") ?? [];
      parentForm.setValue(
        "faxes",
        [...existingFaxes, { ...data, id: Math.random().toString() }],
        {
          shouldDirty: true,
        },
      );
      return data;
    }
  };

  const onSubmit = async (data: z.infer<typeof formSchema>) => {
    if (parentForm) {
      onSubmitParentForm(data);
      setOpen(false);
      return;
    }
    if (editing) {
      const dirtyKeys = Object.keys(form.formState.dirtyFields) as Array<
        keyof z.infer<typeof formSchema>
      >;
      const dirtyData: Partial<z.infer<typeof formSchema>> = {};
      for (const key of dirtyKeys) {
        (dirtyData as Record<string, unknown>)[key] = data[key];
      }
      await updateContactFax.mutateAsync({
        contactId,
        id: activeContactFax.id,
        ...dirtyData,
        dateModified: activeContactFax.dateModified,
      });
    } else {
      await createContactFax
        .mutateAsync({
          contactId,
          ...data,
          dateModified: undefined,
        })
        .then(() => {
          form.reset();
        });
    }
    setOpen(false);
  };

  useEffect(() => {
    if (!open) {
      form.reset();
    }
  }, [open, form]);

  const title = editing ? "Update fax number" : "Create fax number";
  const isInline = variant === "inline";
  const fieldsetClassName = cn(
    slideoverFieldsetClassName,
    isInline && "p-0 my-2 gap-3",
  );
  const handleInlineSubmit = () => {
    void form.handleSubmit(onSubmit)();
  };
  const formContent = (
    <Form {...form}>
      <form
        className={cn(
          "relative flex grow flex-col",
          isInline ? "overflow-visible" : "overflow-hidden",
        )}
        onSubmit={async (e) => {
          e.preventDefault();
          e.stopPropagation();
          await form.handleSubmit(onSubmit)();
        }}
      >
        {isFormLoading && <FormLoadingOverlay />}
        <div
          className={cn(
            "overflow-y-auto",
            variant === "inline" && "overflow-visible",
          )}
        >
          <fieldset
            disabled={editing ? !hasEditPermission : false}
            className={fieldsetClassName}
          >
            <SimpleSelectFormField
              placeholder="Select a label"
              label="Label"
              register={form.register("label")}
              items={CONTACT_FAX_LABELS}
            />
            <CheckboxFormField
              register={form.register("isPrimary")}
              label="Primary fax number"
              className={variant === "inline" ? "mt-0 h-auto" : undefined}
            />
            <PhoneNumberFormField
              register={form.register("number")}
              label="Fax number*"
            />
            <TextFormField
              register={form.register("extension")}
              label="Extension"
            />
          </fieldset>
        </div>
        <div className="flex grow" />
        <SheetFooter addSlideoverStyles={variant === "slideover"}>
          {hasDeletePermission && editing && (
            <Button type="button" variant="destructive" onClick={onDelete}>
              Delete
            </Button>
          )}
          <div className="flex grow justify-end gap-x-2">
            <FormCancelButton
              setOpen={setOpen}
              isDirty={form.formState.isDirty}
            />
            <FormSubmitButton
              type={isInline ? "button" : "submit"}
              submitFn={isInline ? handleInlineSubmit : undefined}
            />
          </div>
        </SheetFooter>
      </form>
    </Form>
  );

  if (variant === "inline") {
    if (!open) return null;
    return (
      <div className="space-y-4">
        <div className="flex items-start gap-2">
          <ContactFaxIcon className="h-5 w-5 text-gray-400" />
          <div className="space-y-1">
            <Typography weight="medium">{title}</Typography>
          </div>
        </div>
        {formContent}
      </div>
    );
  }

  return (
    <Slideover
      open={open}
      setOpen={setOpen}
      isDirty={form.formState.isDirty}
      title={title}
      titleIcon={<ContactFaxIcon className="h-4 w-4" />}
      isLoading={isFormLoading}
    >
      {formContent}
    </Slideover>
  );
};
